class ModuleTreeManager {
    constructor() {
      this.idToNodeMap = new Map();
    }
  
    preprocessModules(modules) {
      if (!Array.isArray(modules)) {
        throw new Error('Modules must be an array');
      }
  
      this.idToNodeMap.clear();
      
      const processNode = (node, parent = null) => {
        if (!node?.id) {
          throw new Error('Node must have a valid id');
        }
        
        node.parent = parent;
        this.idToNodeMap.set(node.id, node);
        
        if (node.children?.length) {
          node.children.forEach(child => processNode(child, node));
        }
      };
  
      modules.forEach(root => processNode(root));
      return this.idToNodeMap;
    }
  
    addModuleToTree(newNode, clonedModules) {
      if (!newNode?.id) {
        throw new Error('New node must have a valid id');
      }
  
      const nodeToInsert = {
        ...newNode,
        children: [],
        totalcount: newNode.individualcount ?? 0,
        parent: null,
      };
  
      if (nodeToInsert.parent_id) {
        const parent = this.idToNodeMap.get(nodeToInsert.parent_id);
        if (!parent) {
          throw new Error(`Parent node with id ${nodeToInsert.parent_id} not found`);
        }
  
        nodeToInsert.parent = parent;
        parent.children = parent.children || [];
        parent.children.push(nodeToInsert);
      } else {
        clonedModules.push(nodeToInsert);
      }
  
      this.idToNodeMap.set(nodeToInsert.id, nodeToInsert);
      this.updateAncestorCounts(nodeToInsert.parent);
  
      return this.sanitizeModules(clonedModules);
    }
  
    deleteNode(deleteId, clonedModules) {
      const node = this.idToNodeMap.get(deleteId);
      if (!node) {
        throw new Error(`Node with id ${deleteId} not found`);
      }
  
      if (node.parent) {
        node.parent.children = node.parent.children.filter(child => child.id !== deleteId);
      } else {
        clonedModules = clonedModules.filter(mod => mod.id !== deleteId);
      }
  
      this.removeDescendants(node);
      this.updateAncestorCounts(node.parent);
  
      return this.sanitizeModules(clonedModules);
    }
  
    updateIndividualCount(id, newIndividualCount, clonedModules) {
      const node = this.idToNodeMap.get(id);
      if (!node) {
        throw new Error(`Node with id ${id} not found`);
      }
  
      node.individualcount = Math.max(0, newIndividualCount);
      this.updateAncestorCounts(node);
  
      return this.sanitizeModules(clonedModules);
    }
  
    addTestCaseCount(moduleId, count = 1, clonedModules) {
      const node = this.idToNodeMap.get(moduleId);
      if (!node) {
        throw new Error(`Module with id ${moduleId} not found`);
      }
  
      const updatedCount = (node.individualcount ?? 0) + count;
      return this.updateIndividualCount(moduleId, updatedCount, clonedModules);
    }
  
    moveTestCaseCount(sourceModuleId, targetModuleId, count = 1, clonedModules) {
      const sourceNode = this.idToNodeMap.get(sourceModuleId);
      const targetNode = this.idToNodeMap.get(targetModuleId);
      
      if (!sourceNode) {
        throw new Error(`Source module with id ${sourceModuleId} not found`);
      }
      if (!targetNode) {
        throw new Error(`Target module with id ${targetModuleId} not found`);
      }
  
      const availableCount = sourceNode.individualcount ?? 0;
      const actualCount = Math.min(count, availableCount);
      
      if (actualCount <= 0) {
        return clonedModules;
      }
  
      let updatedModules = this.updateIndividualCount(sourceModuleId, availableCount - actualCount, clonedModules);
      this.preprocessModules(updatedModules);
      updatedModules = this.updateIndividualCount(targetModuleId, (targetNode.individualcount ?? 0) + actualCount, updatedModules);
  
      return updatedModules;
    }
  
    deleteTestCaseCount(moduleId, count = 1, clonedModules) {
      const node = this.idToNodeMap.get(moduleId);
      if (!node) {
        throw new Error(`Module with id ${moduleId} not found`);
      }
  
      const currentCount = node.individualcount ?? 0;
      const updatedCount = Math.max(0, currentCount - count);
      
      return this.updateIndividualCount(moduleId, updatedCount, clonedModules);
    }
  
    updateAncestorCounts(startNode) {
      const updateTotal = (node) => {
        if (!node) return 0;
        
        const childrenTotal = (node.children || []).reduce(
          (sum, child) => sum + updateTotal(child),
          0
        );
        
        node.totalcount = (node.individualcount ?? 0) + childrenTotal;
        return node.totalcount;
      };
  
      const bubbleUp = (node) => {
        if (!node) return;
        
        updateTotal(node);
        if (node.parent) {
          bubbleUp(node.parent);
        }
      };
  
      bubbleUp(startNode);
    }
  
    removeDescendants(node) {
      if (!node) return;
      
      this.idToNodeMap.delete(node.id);
      (node.children || []).forEach(child => this.removeDescendants(child));
    }
  
    sanitizeModules(modules) {
      const cleanNode = (node) => {
        const cleaned = { ...node };
        delete cleaned.parent;
        cleaned.children = (cleaned.children || []).map(cleanNode);
        return cleaned;
      };
  
      return modules.map(cleanNode);
    }
  }
  
  export const moduleTreeManager = new ModuleTreeManager();
  
  export function preprocessModules(modules) {
    return moduleTreeManager.preprocessModules(modules);
  }
  
  export function addModuleToTree(idToNodeMap, newNode, clonedModules) {
    moduleTreeManager.idToNodeMap = idToNodeMap;
    return moduleTreeManager.addModuleToTree(newNode, clonedModules);
  }
  
  export function deleteNode(idToNodeMap, deleteId, clonedModules) {
    moduleTreeManager.idToNodeMap = idToNodeMap;
    return moduleTreeManager.deleteNode(deleteId, clonedModules);
  }
  
  export function updateIndividualCount(idToNodeMap, id, newIndividualCount, clonedModules) {
    moduleTreeManager.idToNodeMap = idToNodeMap;
    return moduleTreeManager.updateIndividualCount(id, newIndividualCount, clonedModules);
  }